(() => {
  /* =========================================
     CONFIGURATION
     ========================================= */

  const BARBA_WRAPPER_SELECTOR =
    '.page-wrapper__content[data-barba="wrapper"]';

  const HEADER_MOTION_SELECTOR =
    '.wambolt-header, .wambolt-mobile-nav__panel';

  const FOOTER_MOTION_SELECTOR =
    '.page-wrapper__content > footer';

  const HEADER_TRANSITION_PROPERTY =
    '--wambolt-page-transition-y';

  const TRANSITION_WRAP_SELECTOR =
    '[data-transition-wrap]';

  const TRANSITION_PANEL_SELECTOR =
    '[data-transition-panel]';

  const TRANSITION_LABEL_SELECTOR =
    '[data-transition-label]';

  const TRANSITION_LABEL_TEXT_SELECTOR =
    '[data-transition-label-text]';

  const DEFAULT_DURATION = 0.6;

  const DEFAULT_EASE =
    'wambolt-page-transition-ease';


  /* =========================================
     DESIGN MODE
     ========================================= */

  const isDesignMode =
    document.documentElement.classList.contains(
      'shopify-design-mode'
    ) ||
    Boolean(
      window.Shopify &&
      window.Shopify.designMode
    );

  if (isDesignMode) {
    return;
  }


  /* =========================================
     DEPENDENCIES
     ========================================= */

  if (
    typeof window.barba === 'undefined' ||
    typeof window.gsap === 'undefined' ||
    typeof window.CustomEase === 'undefined'
  ) {
    console.warn(
      'Wambolt page transitions could not initialize because Barba, GSAP, or CustomEase is unavailable.'
    );

    return;
  }

  gsap.registerPlugin(CustomEase);

  CustomEase.create(
    DEFAULT_EASE,
    '0.625, 0.05, 0, 1'
  );

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }


  /* =========================================
     REDUCED MOTION
     ========================================= */

  const reducedMotionQuery =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

  let reducedMotion =
    reducedMotionQuery.matches;

  function handleReducedMotionChange(event) {
    reducedMotion = event.matches;
  }

  if (
    typeof reducedMotionQuery.addEventListener ===
    'function'
  ) {
    reducedMotionQuery.addEventListener(
      'change',
      handleReducedMotionChange
    );
  } else if (
    typeof reducedMotionQuery.addListener ===
    'function'
  ) {
    reducedMotionQuery.addListener(
      handleReducedMotionChange
    );
  }


  /* =========================================
     GSAP TIMELINE
     ========================================= */

  function createTransitionTimeline(
    options = {}
  ) {
    return gsap.timeline({
      defaults: {
        ease: DEFAULT_EASE,
        duration: DEFAULT_DURATION,
      },
      ...options,
    });
  }


  /* =========================================
     PERSISTENT HEADER MOTION
     ========================================= */

  function getHeaderMotionTargets() {
    return Array.from(
      document.querySelectorAll(
        HEADER_MOTION_SELECTOR
      )
    );
  }

  function clearHeaderOffset() {
    getHeaderMotionTargets().forEach(
      (element) => {
        element.style.removeProperty(
          HEADER_TRANSITION_PROPERTY
        );
      }
    );
  }

    /* =========================================
     PERSISTENT FOOTER MOTION
     ========================================= */

  function getFooterMotionTarget() {
    return document.querySelector(
      FOOTER_MOTION_SELECTOR
    );
  }


  function clearFooterOffset() {
    const footer =
      getFooterMotionTarget();

    if (!footer) {
      return;
    }

    gsap.set(
      footer,
      {
        clearProps: 'transform',
      }
    );
  }


  function hidePersistentFooter() {
    document.documentElement.setAttribute(
      'data-wambolt-footer-transition-hidden',
      'true'
    );
  }


  function showPersistentFooter() {
    document.documentElement.removeAttribute(
      'data-wambolt-footer-transition-hidden'
    );
  }


  /* =========================================
     LENIS
     ========================================= */

  function getLenis() {
    return window.wamboltLenis || null;
  }

  function stopLenis() {
    const lenis = getLenis();

    if (
      lenis &&
      typeof lenis.stop === 'function'
    ) {
      lenis.stop();
    }
  }

  function startLenis() {
    const lenis = getLenis();

    if (
      lenis &&
      typeof lenis.start === 'function'
    ) {
      lenis.start();
    }
  }


  /* =========================================
     SCROLL RESET
     ========================================= */

  function scrollToTop() {
    const lenis = getLenis();

    if (
      lenis &&
      typeof lenis.scrollTo === 'function'
    ) {
      lenis.scrollTo(0, {
        immediate: true,
        force: true,
      });
    }

    const pageWrapper =
      document.querySelector(
        '.page-wrapper'
      );

    if (pageWrapper) {
      pageWrapper.scrollTop = 0;
    }

    window.scrollTo(0, 0);
  }

  function refreshScrollSystems() {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const lenis = getLenis();

        if (
          lenis &&
          typeof lenis.resize === 'function'
        ) {
          lenis.resize();
        }

        if (
          window.ScrollTrigger &&
          typeof ScrollTrigger.refresh ===
            'function'
        ) {
          ScrollTrigger.refresh();
        }
      });
    });
  }


  /* =========================================
     TRANSITION LOCK
     ========================================= */

    function beginPageTransition() {
    /*
     * Defensive reset in case a previous transition
     * was interrupted.
     */
    showPersistentFooter();
    clearFooterOffset();

    document.documentElement.setAttribute(
      'data-wambolt-page-transitioning',
      'true'
    );

    stopLenis();
  }


  function endPageTransition() {
    /*
     * At this point the incoming main has returned
     * to normal document flow, so the persistent
     * footer can safely become visible again.
     */
    clearFooterOffset();
    showPersistentFooter();

    document.documentElement.removeAttribute(
      'data-wambolt-page-transitioning'
    );

    startLenis();
    refreshScrollSystems();
  }


  /* =========================================
     CONTAINER POSITION
     ========================================= */

  function placeNextContainer(next) {
    const wrapper =
      document.querySelector(
        BARBA_WRAPPER_SELECTOR
      );

    if (!wrapper || !next) {
      return;
    }

    const footer =
      Array.from(wrapper.children).find(
        (element) =>
          element.tagName === 'FOOTER'
      );

    if (
      footer &&
      next.parentNode === wrapper
    ) {
      wrapper.insertBefore(
        next,
        footer
      );
    }
  }


  /* =========================================
     MOBILE NAVIGATION
     ========================================= */

  function closePersistentNavigation() {
    const nav =
      document.querySelector(
        '.wambolt-mobile-nav[data-navigation-status="active"]'
      );

    if (!nav) {
      return;
    }

    const closeButton =
      nav.querySelector(
        '[data-navigation-toggle="close"]'
      );

    if (closeButton) {
      closeButton.click();
    }
  }


  /* =========================================
     SHOPIFY / WAMBOLT LIFECYCLE
     ========================================= */

  function dispatchShopifyLifecycle(
    container,
    type
  ) {
    if (!container) {
      return;
    }

    container.dispatchEvent(
      new CustomEvent(type, {
        bubbles: true,
        detail: {
          source: 'barba',
        },
      })
    );
  }

  function killScrollTriggersIn(container) {
    if (
      !container ||
      !window.ScrollTrigger ||
      typeof ScrollTrigger.getAll !==
        'function'
    ) {
      return;
    }

    ScrollTrigger.getAll().forEach(
      (trigger) => {
        const triggerElement =
          trigger.trigger;

        const pinElement =
          trigger.pin;

        const triggerBelongsToPage =
          triggerElement instanceof Element &&
          container.contains(
            triggerElement
          );

        const pinBelongsToPage =
          pinElement instanceof Element &&
          container.contains(
            pinElement
          );

        if (
          triggerBelongsToPage ||
          pinBelongsToPage
        ) {
          trigger.kill();
        }
      }
    );
  }

  function cleanupCurrentPage(container) {
    if (!container) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(
        'wambolt:page:before-leave',
        {
          detail: {
            container,
          },
        }
      )
    );

    /*
     * Individual Wambolt components own teardown.
     * Barba only dispatches the shared lifecycle event.
     */
    dispatchShopifyLifecycle(
      container,
      'shopify:section:unload'
    );

    /*
     * Remove any remaining page-owned ScrollTriggers
     * defensively after component cleanup has run.
     */
    killScrollTriggersIn(container);
  }

  function activateMarkedScripts(container) {
    if (!container) {
      return;
    }

    container
      .querySelectorAll(
        'script[data-barba-reload]'
      )
      .forEach((oldScript) => {
        const type =
          (
            oldScript.getAttribute('type') ||
            ''
          ).trim();

        const executable =
          !type ||
          type === 'text/javascript' ||
          type === 'application/javascript' ||
          type === 'module';

        if (!executable) {
          return;
        }

        const newScript =
          document.createElement(
            'script'
          );

        Array.from(
          oldScript.attributes
        ).forEach((attribute) => {
          newScript.setAttribute(
            attribute.name,
            attribute.value
          );
        });

        newScript.textContent =
          oldScript.textContent;

        oldScript.replaceWith(
          newScript
        );
      });
  }

  function initializeIncomingPage(container) {
    if (!container) {
      return;
    }

    activateMarkedScripts(container);

    dispatchShopifyLifecycle(
      container,
      'shopify:section:load'
    );

    window.dispatchEvent(
      new CustomEvent(
        'wambolt:page:enter',
        {
          detail: {
            container,
          },
        }
      )
    );

    refreshScrollSystems();
  }


  /* =========================================
     DOCUMENT METADATA
     ========================================= */

  function parseNextDocument(html) {
    if (!html) {
      return null;
    }

    return new DOMParser().parseFromString(
      html,
      'text/html'
    );
  }

  function updateDocumentMetadata(html) {
    const nextDocument =
      parseNextDocument(html);

    if (!nextDocument) {
      return;
    }

    if (nextDocument.title) {
      document.title =
        nextDocument.title;
    }

    const nextLanguage =
      nextDocument.documentElement.getAttribute(
        'lang'
      );

    if (nextLanguage) {
      document.documentElement.setAttribute(
        'lang',
        nextLanguage
      );
    }
  }


  /* =========================================
     PERSISTENT NAVIGATION UPDATE
     ========================================= */

  function updatePersistentNavigation(data) {
    if (
      !data ||
      !data.next ||
      !data.next.html
    ) {
      return;
    }

    const nextDocument =
      parseNextDocument(
        data.next.html
      );

    if (!nextDocument) {
      return;
    }

    const nextNodes =
      nextDocument.querySelectorAll(
        '#header-group [data-barba-update]'
      );

    const currentNodes =
      document.querySelectorAll(
        '#header-group [data-barba-update]'
      );

    currentNodes.forEach(
      (current, index) => {
        const next =
          nextNodes[index];

        if (!next) {
          return;
        }

        const newStatus =
          next.getAttribute(
            'aria-current'
          );

        if (newStatus !== null) {
          current.setAttribute(
            'aria-current',
            newStatus
          );
        } else {
          current.removeAttribute(
            'aria-current'
          );
        }

        current.setAttribute(
          'class',
          next.getAttribute('class') ||
            ''
        );
      }
    );
  }


  /* =========================================
     RESET PAGE
     ========================================= */

  function resetPage(container) {
    scrollToTop();

    gsap.set(container, {
      clearProps:
        'position,top,left,right,transform,opacity,visibility',
    });

    /*
     * The transition finishes at zero header offset,
     * so return transform ownership entirely to the
     * persistent header stylesheet.
     */
    clearHeaderOffset();

    const lenis = getLenis();

    if (
      lenis &&
      typeof lenis.resize === 'function'
    ) {
      lenis.resize();
    }
  }


  /* =========================================
     PAGE TRANSITIONS
     ========================================= */

  function runPageOnceAnimation(next) {
    const timeline =
      createTransitionTimeline();

    timeline.call(
      () => {
        resetPage(next);
      },
      null,
      0
    );

    return timeline;
  }

  function runPageLeaveAnimation(
    current,
    next
  ) {
    const transitionWrap =
      document.querySelector(
        TRANSITION_WRAP_SELECTOR
      );

    const transitionPanel =
      transitionWrap?.querySelector(
        TRANSITION_PANEL_SELECTOR
      );

    const transitionLabel =
      transitionWrap?.querySelector(
        TRANSITION_LABEL_SELECTOR
      );

    const transitionLabelText =
      transitionWrap?.querySelector(
        TRANSITION_LABEL_TEXT_SELECTOR
      );

    const headerTargets =
      getHeaderMotionTargets();

    const footerTarget =
      getFooterMotionTarget();

    if (
      !transitionWrap ||
      !transitionPanel ||
      !transitionLabel ||
      !transitionLabelText
    ) {
      return gsap.to(current, {
        autoAlpha: 0,
        duration: 0.4,
      });
    }

    const nextPageName =
      next.getAttribute(
        'data-page-name'
      );

    if (nextPageName) {
      transitionLabelText.textContent =
        nextPageName;
    }

    const timeline =
      createTransitionTimeline({
        onComplete: () => {
          if (
            current &&
            current.isConnected
          ) {
            current.remove();
          }
        },
      });

    if (reducedMotion) {
      timeline.call(
        scrollToTop,
        null,
        0
      );

      timeline.call(
        hidePersistentFooter,
        null,
        0
      );

      timeline.call(
        clearFooterOffset,
        null,
        0
      );

      if (headerTargets.length) {
        timeline.set(
          headerTargets,
          {
            [HEADER_TRANSITION_PROPERTY]:
              '0vh',
          },
          0
        );
      }

      return timeline.set(
        current,
        {
          autoAlpha: 0,
        }
      );
    }

    timeline.set(
      transitionPanel,
      {
        autoAlpha: 1,
      },
      0
    );

    timeline.set(
      next,
      {
        autoAlpha: 0,
      },
      0
    );

    if (headerTargets.length) {
      timeline.set(
        headerTargets,
        {
          [HEADER_TRANSITION_PROPERTY]:
            '0vh',
        },
        0
      );
    }

    /*
     * Panel rises over the outgoing page.
     */
    timeline.fromTo(
      transitionPanel,
      {
        yPercent: 0,
      },
      {
        yPercent: -100,
        duration: 0.8,
      },
      0
    );

    timeline.fromTo(
      transitionLabel,
      {
        autoAlpha: 0,
      },
      {
        autoAlpha: 1,
      },
      '<+=0.2'
    );

    /*
     * Outgoing page and persistent header
     * rise together.
     */
    timeline.fromTo(
      current,
      {
        y: '0vh',
      },
      {
        y: '-15vh',
        duration: 0.8,
      },
      0
    );

    /*
     * The persistent footer is outside the Barba
     * container, so move its OUTER wrapper with the
     * outgoing page during leave.
     *
     * This does not interfere with the footer's
     * internal parallax transform.
     */
    if (footerTarget) {
      timeline.fromTo(
        footerTarget,
        {
          y: '0vh',
        },
        {
          y: '-15vh',
          duration: 0.8,
        },
        0
      );
    }

    if (headerTargets.length) {
      timeline.to(
        headerTargets,
        {
          [HEADER_TRANSITION_PROPERTY]:
            '-15vh',
          duration: 0.8,
        },
        0
      );
    }

    /*
     * Reset scrolling once the transition panel
     * completely covers the viewport.
     */
    timeline.call(
      scrollToTop,
      null,
      0.8
    );

    /*
     * The dark transition panel now covers the
     * entire viewport.
     *
     * Only now hide the persistent footer while
     * the incoming Barba container is fixed.
     */
    timeline.call(
      hidePersistentFooter,
      null,
      0.8
    );

    /*
     * Remove the temporary footer transform while
     * it is safely hidden behind the transition.
     */
    timeline.call(
      clearFooterOffset,
      null,
      0.8
    );

    return timeline;
  }

  function runPageEnterAnimation(next) {
    const transitionWrap =
      document.querySelector(
        TRANSITION_WRAP_SELECTOR
      );

    const transitionPanel =
      transitionWrap?.querySelector(
        TRANSITION_PANEL_SELECTOR
      );

    const transitionLabel =
      transitionWrap?.querySelector(
        TRANSITION_LABEL_SELECTOR
      );

    const headerTargets =
      getHeaderMotionTargets();

    if (
      !transitionWrap ||
      !transitionPanel ||
      !transitionLabel
    ) {
      const fallback =
        createTransitionTimeline();

      fallback.set(
        next,
        {
          autoAlpha: 1,
        }
      );

      fallback.call(
        resetPage,
        [next]
      );

      return new Promise(
        (resolve) => {
          fallback.call(resolve);
        }
      );
    }

    const timeline =
      createTransitionTimeline();

    if (reducedMotion) {
      timeline.set(
        next,
        {
          autoAlpha: 1,
        }
      );

      if (headerTargets.length) {
        timeline.set(
          headerTargets,
          {
            [HEADER_TRANSITION_PROPERTY]:
              '0vh',
          }
        );
      }

      timeline.add(
        'pageReady'
      );

      timeline.call(
        resetPage,
        [next],
        'pageReady'
      );

      return new Promise(
        (resolve) => {
          timeline.call(
            resolve,
            null,
            'pageReady'
          );
        }
      );
    }

    /*
     * The outgoing header reaches -15vh at 0.8s.
     * Move it below the viewport while covered so
     * it can enter with the incoming page.
     */
    if (headerTargets.length) {
      timeline.set(
        headerTargets,
        {
          [HEADER_TRANSITION_PROPERTY]:
            '15vh',
        },
        0.81
      );
    }

    /*
     * Preserve the established covered-screen hold.
     */
    timeline.add(
      'startEnter',
      1.25
    );

    timeline.set(
      next,
      {
        autoAlpha: 1,
      },
      'startEnter'
    );

    timeline.fromTo(
      transitionPanel,
      {
        yPercent: -100,
      },
      {
        yPercent: -200,
        duration: 1,
        overwrite: 'auto',
        immediateRender: false,
      },
      'startEnter'
    );

    timeline.set(
      transitionPanel,
      {
        autoAlpha: 0,
      },
      '>'
    );

    timeline.fromTo(
      transitionLabel,
      {
        autoAlpha: 1,
      },
      {
        autoAlpha: 0,
        duration: 0.4,
        overwrite: 'auto',
        immediateRender: false,
      },
      'startEnter+=0.1'
    );

    /*
     * Incoming page and persistent header
     * rise together.
     */
    timeline.from(
      next,
      {
        y: '15vh',
        duration: 1,
      },
      'startEnter'
    );

    if (headerTargets.length) {
      timeline.to(
        headerTargets,
        {
          [HEADER_TRANSITION_PROPERTY]:
            '0vh',
          duration: 1,
        },
        'startEnter'
      );
    }

    timeline.add(
      'pageReady'
    );

    timeline.call(
      resetPage,
      [next],
      'pageReady'
    );

    return new Promise(
      (resolve) => {
        timeline.call(
          resolve,
          null,
          'pageReady'
        );
      }
    );
  }


  /* =========================================
     BARBA PREVENT RULES
     ========================================= */

  function shouldPreventBarba({
    href,
  }) {
    if (!href) {
      return false;
    }

    let url;

    try {
      url = new URL(
        href,
        window.location.href
      );
    } catch (error) {
      return true;
    }

    const pathname =
      url.pathname;

    if (
      pathname === '/account/logout' ||
      pathname === '/cart' ||
      pathname.startsWith(
        '/cart/'
      ) ||
      pathname === '/account' ||
      pathname.startsWith(
        '/account/'
      ) ||
      pathname === '/password'
    ) {
      return true;
    }

    if (
      pathname === '/checkout' ||
      pathname.startsWith(
        '/checkout/'
      ) ||
      pathname === '/checkouts' ||
      pathname.startsWith(
        '/checkouts/'
      ) ||
      pathname === '/challenge' ||
      pathname.startsWith(
        '/challenge/'
      )
    ) {
      return true;
    }

    return false;
  }


  /* =========================================
     BARBA HOOKS
     ========================================= */

  barba.hooks.beforeLeave(
    (data) => {
      beginPageTransition();

      closePersistentNavigation();

      cleanupCurrentPage(
        data.current.container
      );
    }
  );

  barba.hooks.beforeEnter(
    (data) => {
      placeNextContainer(
        data.next.container
      );

      gsap.set(
        data.next.container,
        {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
        }
      );

      updateDocumentMetadata(
        data.next.html
      );
    }
  );

  barba.hooks.enter(
    (data) => {
      updatePersistentNavigation(
        data
      );
    }
  );

  barba.hooks.afterEnter(
    (data) => {
      initializeIncomingPage(
        data.next.container
      );

      endPageTransition();
    }
  );


  /* =========================================
     BARBA INITIALIZATION
     ========================================= */

  barba.init({
    debug: false,
    timeout: 7000,
    preventRunning: true,
    prevent: shouldPreventBarba,

    transitions: [
      {
        name:
          'wambolt-page-transition',

        sync: true,

        async once(data) {
          return runPageOnceAnimation(
            data.next.container
          );
        },

        async leave(data) {
          return runPageLeaveAnimation(
            data.current.container,
            data.next.container
          );
        },

        async enter(data) {
          return runPageEnterAnimation(
            data.next.container
          );
        },
      },
    ],
  });
})();